// The app's modal: always import this, never @drawbotics/modal directly (ESLint enforces it).
//
// @drawbotics/modal 3.3 handles the body scroll lock itself with
// `useEffect(() => { body.style.overflow = isVisible ? 'hidden' : 'initial' }, [isVisible])`:
// it writes on mount (so a hidden modal mounting unlocks an open one) and never restores
// on unmount (so a modal unmounted while visible, e.g. closed and navigated away from in
// one batched update, leaves the page unscrollable). This wrapper owns the lock instead,
// through ScrollLock (the ref-counted utils/scroll-lock):
// - while isVisible, it holds a lock, released on close and on unmount;
// - after every commit where the library may have written (mount, isVisible change), it
//   writes the overflow back from the lock count. ScrollLock is a later sibling of the
//   library modal, so its effect runs after the library's own effect in the same commit
//   (passive effects run depth first, children before parents and siblings in order;
//   components/__tests__/Modal.test.jsx checks that ordering).
import React, { useCallback, useEffect, useRef, useState } from 'react';
// eslint-disable-next-line no-restricted-imports
import LibraryModal, { BaseModal } from '@drawbotics/modal';

import ScrollLock from './ScrollLock';


const Modal = (props) => {
  return (
    <>
      <LibraryModal {...props} />
      <ScrollLock active={!!props.isVisible} />
    </>
  );
};


// Same API as @drawbotics/modal's ConfirmModal, built on the wrapper above (the library's
// one renders its own Modal, out of reach of the lock). `confirm()` returns a promise of
// the answer; a pending one resolves to false if confirm() is called again or the
// ConfirmModal unmounts, so callers awaiting it never hang. `children` gets
// (onClickConfirm, onClickCancel, isVisible): the content stays mounted during the
// close transition, so a child with its own visibility side effects (a react-drylus
// Modal) can be passed `isVisible` and undo them itself.
export const ConfirmModal = ({ enhance, children }) => {
  const [ isVisible, setIsVisible ] = useState(false);
  const resolveRef = useRef(null);

  const settle = useCallback((confirmed) => {
    const resolve = resolveRef.current;
    resolveRef.current = null;
    resolve?.(confirmed);
  }, []);

  const confirm = useCallback(() => new Promise((resolve) => {
    settle(false);
    resolveRef.current = resolve;
    setIsVisible(true);
  }), [settle]);

  useEffect(() => () => settle(false), [settle]);

  const onClickConfirm = useCallback(() => {
    settle(true);
    setIsVisible(false);
  }, [settle]);
  const onClickCancel = useCallback(() => {
    settle(false);
    setIsVisible(false);
  }, [settle]);

  return (
    <>
      {enhance(confirm)}
      <Modal isVisible={isVisible} onClickOverlay={onClickCancel}>
        {children(onClickConfirm, onClickCancel, isVisible)}
      </Modal>
    </>
  );
};


export { BaseModal };

export default Modal;
